import type { Database } from "@/integrations/supabase/types";

export type ProductRow = Database["public"]["Tables"]["products"]["Row"];
export type ProductImageRow = Database["public"]["Tables"]["product_images"]["Row"];

export type ProductWithImagesRow = ProductRow & {
  product_images: ProductImageRow[];
};

export type ProductListItem = {
  id: string;
  reference: string;
  name: string;
  price: number;
  list_price: number;
  category: string;
  subcategory: string | null;
  brand: string | null;
  variant: string[];
  material: string | null;
  type: string | null;
  tags: string[] | null;
  aro: number | null;
  video_url: string | null;
  image: string;
  colors: string[];
  gender: string | null;
  age_group: string | null;
  created_at: string;
  is_featured: boolean;
};

export type RelatedProduct = {
  id: string;
  name: string;
  reference: string;
  price: number;
  category: string;
  image: string;
  color: string;
};

export function sortImages(images: ProductImageRow[]): ProductImageRow[] {
  return [...images].sort((a, b) => a.sort_order - b.sort_order);
}

export function getFirstImageUrl(images: ProductImageRow[]): string {
  return sortImages(images)[0]?.image_url ?? "";
}

export function getColors(images: ProductImageRow[]): string[] {
  // Ignora nomes vazios/de fundo de foto. Só retorna variações de cor reais
  // (produtos com foto por cor). Acessórios usam o campo `available_colors`.
  return [...new Set(images.map((i) => (i.color_name ?? "").trim()).filter((c) => c.length > 0))];
}

// Cores listadas em products.available_colors (texto separado por vírgula).
// Usado por acessórios/fornituras, que não têm foto por cor.
export function splitAvailableColors(available: string | null | undefined): string[] {
  if (!available) return [];
  return [...new Set(available.split(",").map((c) => c.trim()).filter((c) => c.length > 0))];
}

export function mapToProductListItem(row: ProductWithImagesRow): ProductListItem {
  const sorted = sortImages(row.product_images);
  // Variações por foto (color_name) têm prioridade; se não houver, usa a lista
  // de cores cadastrada em available_colors (acessórios, fornituras, etc.).
  const imgColors = getColors(row.product_images);
  const colors = imgColors.length > 0 ? imgColors : splitAvailableColors(row.available_colors);
  return {
    id: row.id,
    reference: row.reference,
    name: row.name,
    price: row.price,
    list_price: row.list_price ?? 0,
    category: row.category,
    subcategory: row.subcategory ?? null,
    brand: (row as { brand?: string | null }).brand ?? null,
    variant: splitAvailableColors(row.variant),
    material: row.material,
    type: row.type,
    tags: row.tags,
    aro: row.aro ?? null,
    video_url: row.video_url ?? null,
    image: sorted[0]?.image_url ?? "",
    colors,
    gender: (row as { gender?: string | null }).gender ?? null,
    age_group: (row as { age_group?: string | null }).age_group ?? null,
    created_at: row.created_at,
    is_featured: row.is_featured,
  };
}

export type ProductCostRow = Database["public"]["Tables"]["product_costs"]["Row"];

export type AdminProductRow = ProductRow & {
  product_images: Pick<ProductImageRow, "image_url" | "color_name" | "sort_order">[];
  product_costs: Pick<ProductCostRow, "cost" | "supplier" | "data_cadastro">[];
};

export function mapToRelatedProduct(row: Pick<ProductRow, "id" | "name" | "reference" | "price" | "category"> & { product_images: ProductImageRow[] }): RelatedProduct {
  const sorted = sortImages(row.product_images);
  return {
    id: row.id,
    name: row.name,
    reference: row.reference,
    price: row.price,
    category: row.category,
    image: sorted[0]?.image_url ?? "",
    color: sorted[0]?.color_name ?? "",
  };
}
